// logic/integrationlogic/hooks/useInvalidCredentialLock.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { secureStorage } from '../../utils/secureStorage';
import logger from '../../services/logging/logger';

/**
 * Hook to manage invalid credential lock state
 * When credentials are invalid, user is locked to organization-settings page
 * Uses encrypted storage for sensitive data (OWASP A02 mitigation)
 */
export const useInvalidCredentialLock = () => {
  const [isLocked, setIsLocked] = useState(false);
  const [invalidIntegrations, setInvalidIntegrations] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Check encrypted storage on mount and location change
  useEffect(() => {
    const check = async () => {
      const invalidFromStorage = await secureStorage.getEncrypted('invalidIntegrations');

      if (invalidFromStorage) {
        try {
          if (Array.isArray(invalidFromStorage) && invalidFromStorage.length > 0) {
            setIsLocked(true);
            setInvalidIntegrations(invalidFromStorage);

            logger.info('Invalid credentials detected from encrypted storage', {
              count: invalidFromStorage.length
            });

            // If not on org settings page, redirect immediately
            if (location.pathname !== '/organization-settings') {
              navigate('/organization-settings', { replace: true });
            }
          }
        } catch (error) {
          logger.error('Error processing invalid integrations from encrypted storage', {
            error: error.message
          });
        }
      }
    };
    check();
  }, [location.pathname, navigate]);

  // Unlock when credentials are fixed
  const unlock = useCallback(() => {
    // Remove from encrypted storage
    secureStorage.removeEncrypted('invalidIntegrations');
    secureStorage.removeEncrypted('isAdminUser');
    setIsLocked(false);
    setInvalidIntegrations([]);
    logger.info('Invalid credential lock cleared');
  }, []);

  // Check if navigation should be blocked
  const shouldBlockNavigation = useCallback((targetPath) => {
    // Only block if locked and trying to leave org settings
    return isLocked && targetPath !== '/organization-settings';
  }, [isLocked]);

  return {
    isLocked,
    invalidIntegrations,
    unlock,
    shouldBlockNavigation
  };
};